import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCountdown } from "../hooks/useCountdown";
import { useLiveInfo } from "../hooks/useLiveInfo";
import { useRsvpReservation } from "../hooks/useRsvpReservation";
import { shouldOfferPublicCheckIn } from "../lib/public-check-in";
import { RsvpModal } from "./RsvpModal";
import { useEventContent } from "../context/EventContentContext";
import { useSiteCopy } from "../context/SiteCopyContext";
import { CopyText } from "./CopyText";
import {
  formatMonthYearKampala,
  formatOrdinalDayInKampala,
  formatRsvpThanksLine,
  formatWeekdayInKampala,
  hasFeaturedSchedule,
  NEXT_GATHERING_LABEL,
  NEXT_GATHERING_SUB,
} from "../lib/eventDisplay";

export function Hero() {
  const content = useEventContent();
  const { copy } = useSiteCopy();
  const hasSchedule = hasFeaturedSchedule(content);
  const canRsvp = hasSchedule || !content.cancellation;
  const target = useMemo(() => Date.parse(content.date), [content.date]);
  const { d, h, m, s, done } = useCountdown(target);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const reservation = useRsvpReservation(content.eventId);
  const liveInfo = useLiveInfo();
  const checkInOpen = shouldOfferPublicCheckIn(liveInfo, content.eventPhase);
  const checkedIn = Boolean(reservation?.checkedInAt);

  const { day, sup } = formatOrdinalDayInKampala(content.date);
  const weekday = formatWeekdayInKampala(content.date);
  const monthYear = formatMonthYearKampala(content.date);
  const showPoster = Boolean(content.posterUrl);

  return (
    <section className="hero" id="top">
      <div className="hero-aurora"></div>
      <div className="hero-vignette"></div>

      <div className="hero-eyebrow hero-eyebrow-top">
        <span className="rule"></span>
        <span>
          <em>
            <CopyText path="hero.eyebrow" as="span" />
          </em>
        </span>
        <span className="rule"></span>
      </div>

      <div className="hero-inner">
        <h1 className="hero-title">
          <span className="tt-line tt-sm">
            <CopyText path="hero.titleTogether" as="span" />{" "}
            <em>
              <CopyText path="hero.titleIn" as="span" />
            </em>
          </span>
          <span className="tt-line tt-xl">
            <CopyText path="hero.titleHeaven" as="span" />
          </span>
          <span className="tt-line tt-flame">
            <CopyText path="hero.titleFlame" as="span" />
          </span>
        </h1>

        <div className="hero-meta">
          <div className="meta-block">
            <div className="meta-k">
              {hasSchedule ? weekday : "When"}
            </div>
            <div className="meta-v">
              {hasSchedule ? (
                <>
                  {day}
                  <sup>{sup}</sup> {monthYear}
                </>
              ) : (
                <span className="meta-v-sm">{NEXT_GATHERING_LABEL}</span>
              )}
            </div>
            <div className="meta-sub">
              {hasSchedule ? (
                <CopyText path="hero.metaTime" as="span" />
              ) : (
                NEXT_GATHERING_SUB
              )}
            </div>
          </div>
          <div className="meta-divider"></div>
          <div className="meta-block">
            <div className="meta-k">
              <CopyText path="hero.venueK" as="span" />
            </div>
            <div className="meta-v meta-v-sm">
              <CopyText path="hero.venueLine1" as="span" />
            </div>
            <div className="meta-sub">
              <CopyText path="hero.venueLine2" as="span" />
            </div>
          </div>
        </div>

        <div className="hero-cta-row">
          {canRsvp ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setRsvpOpen(true)}
            >
              {checkInOpen
                ? checkedIn
                  ? "You're checked in"
                  : "Check in"
                : reservation
                  ? "Change reservation"
                  : copy.hero.reserveSeat}
            </button>
          ) : (
            <Link to="/event" className="btn btn-primary">
              Event update <span className="arr">→</span>
            </Link>
          )}
          <Link to="/live" className="btn btn-ghost">
            {copy.hero.streamLive} <span className="arr">→</span>
          </Link>
        </div>
        {checkInOpen && checkedIn ? (
          <p className="hero-rsvp-status">
            You’re checked in online
            {reservation?.name.trim() ? ` as ${reservation.name.trim()}` : ""}.
          </p>
        ) : reservation && !checkInOpen ? (
          <p className="hero-rsvp-status">
            You reserved {reservation.seats} seat{reservation.seats === 1 ? "" : "s"}
            {reservation.name.trim() ? ` for ${reservation.name.trim()}` : ""}. You can
            change this anytime.
          </p>
        ) : null}

        {canRsvp && (
        <RsvpModal
          open={rsvpOpen}
          onClose={() => setRsvpOpen(false)}
          eventId={content.eventId}
          thanksLine={
            hasSchedule
              ? formatRsvpThanksLine(content.date)
              : "See you at the next worship overnight."
          }
          title={
            checkInOpen
              ? checkedIn
                ? "You're checked in"
                : "Check in"
              : reservation
                ? "Your reservation"
                : undefined
          }
          mode={checkInOpen ? "check-in" : "rsvp"}
          shareDateIso={hasSchedule ? content.date : undefined}
          shareEventTitle={content.eventTitle}
        />
        )}

        {hasSchedule && !done && (
          <div className="hero-countdown" id="countdown" aria-label="Countdown to event">
          <span className="ct-label">
            <CopyText path="hero.countdownLabel" as="span" />
          </span>
          <span className="ct-val"><b id="ct-d">{d}</b><i>days</i></span>
          <span className="ct-val"><b id="ct-h">{h}</b><i>hrs</i></span>
          <span className="ct-val"><b id="ct-m">{m}</b><i>min</i></span>
          <span className="ct-val"><b id="ct-s">{s}</b><i>sec</i></span>
          </div>
        )}
      </div>

      {showPoster && (
      <div className="hero-poster">
        <img src={content.posterUrl!} alt={copy.hero.posterAlt} />
        <div className="poster-frame"></div>
      </div>
      )}

      <div className="scroll-hint"><span>Scroll</span><i></i></div>
    </section>
  );
}
